import { addNegatives, xScale } from './space/create-scale.js';
import createTypeScale from './type';
import colors, { ThemeColors } from './colors';
import shadows from './shadows';

type Theme = {
  colors: ThemeColors;
  space: {
    [x: string]: string;
  };
  sizes?: {
    [x: string]: string;
  };
  breakpoints: Array<string>;
  fontWeights: Array<number>;
  fonts: { [x: string]: string };
  fontSizes: {
    [x: string]: string;
  };
  lineHeights: { [x: string]: number | string };
  mediaQueries?: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
  };
  shadows: { [x: string]: string };
  letterSpacings: Array<string>;
  radii: { [x: string]: number | string };
  zIndices: Array<number>;
  styles?: any;
};

const scale = xScale(4)(500);

const BASE: Theme = {
  colors,
  space: addNegatives(scale),
  breakpoints: [
    '400px',
    '576px',
    '768px',
    '992px',
    '1024px',
    '1200px',
    '1600px',
  ],
  fontWeights: [0, 100, 200, 300, 400, 500, 600, 700, 800, 900],
  fonts: {
    0: 'Inter, "Helvetica Neue", Arial, sans-serif',
    1: 'Georgia, serif',
    2: 'Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace',
  },
  fontSizes: createTypeScale(),
  lineHeights: {
    0: 0,
    1: 1,
    2: 1.5,
  },
  shadows,
  letterSpacings: ['-0.05em', '-0.025em', '0.015em'],
  radii: {
    0: '0px',
    1: '2px',
  },
  zIndices: [-2, -1, 0, 1, 2, 3, 4],
};

// Set up some named properties
/* eslint-disable prefer-destructuring */
const tokens = BASE;

tokens.fonts.sans = BASE.fonts[0];
tokens.fonts.serif = BASE.fonts[1];
tokens.fonts.mono = BASE.fonts[2];
tokens.fontSizes.root = BASE.fontSizes['1'];
tokens.radii.round = '50%';

tokens.space.release = 'calc(-50vw + 50%)';
tokens.space.root = BASE.space[3];
tokens.lineHeights.root = BASE.lineHeights[1];
tokens.sizes = tokens.space;

tokens.shadows.ghost = 'inset 0 0 0 1px currentColor';

tokens.mediaQueries = {
  xs: `@media screen and (min-width: ${BASE.breakpoints[0]})`,
  sm: `@media screen and (min-width: ${BASE.breakpoints[1]})`,
  md: `@media screen and (min-width: ${BASE.breakpoints[2]})`,
  lg: `@media screen and (min-width: ${BASE.breakpoints[3]})`,
  xl: `@media screen and (min-width: ${BASE.breakpoints[5]})`,
  xxl: `@media screen and (min-width: ${BASE.breakpoints[6]})`,
};
/* eslint-enable prefer-destructuring */

tokens.styles = {
  root: {
    fontFamily: 'sans',
    fontSize: 'root',
    color: 'black',
    backgroundColor: 'white',
    lineHeight: '16px',
    WebkitFontSmoothing: 'subpixel-antialiased',
  },
};

export { Theme, ThemeColors, tokens as default };
